import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Sparkles } from "lucide-react";
import { ProductListing } from "@/components/storefront/ProductListing";
import { PlayfulPageHeader } from "@/components/storefront/PlayfulPageHeader";
import { PlayfulError, PlayfulNotFound } from "@/components/storefront/PlayfulError";
import { listingSearchSchema } from "@/lib/listing-search";
import { ChristmasStrip } from "@/components/storefront/ChristmasStrip";

const newProductsSearchSchema = listingSearchSchema.extend({
  sort: z.enum(["new", "old", "price_asc", "price_desc", "alpha_asc", "alpha_desc", "best"]).default("new"),
});

export const Route = createFileRoute("/new-products")({
  validateSearch: newProductsSearchSchema,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/new-products" },
      { title: "Νέες αφίξεις — MrPanda.gr" },
      { name: "description", content: "Δες τις πιο πρόσφατες νέες αφίξεις στο MrPanda.gr: φρέσκα προϊόντα σε παιχνίδια, είδη σπιτιού και εποχιακά, ανανεωμένα συνεχώς με γρήγορη αποστολή." },
      { property: "og:title", content: "Νέες αφίξεις — MrPanda.gr" },
      { property: "og:description", content: "Οι πιο πρόσφατες νέες αφίξεις στο MrPanda.gr, ανανεωμένες συνεχώς." },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/new-products" }],
  }),
  component: NewProductsPage,
  errorComponent: ({ error, reset }) => (
    <StorefrontLayout>
      <PlayfulError error={error instanceof Error ? error : new Error(String(error))} reset={reset} />
    </StorefrontLayout>
  ),
  notFoundComponent: () => (
    <StorefrontLayout>
      <PlayfulNotFound />
    </StorefrontLayout>
  ),
});

function NewProductsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-10 pt-6">
        <PlayfulPageHeader
          icon={Sparkles}
          title="Νέες αφίξεις"
          subtitle="Ολόφρεσκα προϊόντα, μόλις έφτασαν στη φωλιά του Mr Panda! 🐼"
          mascot="✨"
          doodles={["🆕", "📦", "🌟", "💫"]}
          gradient="bg-gradient-to-br from-sky-200/60 via-accent/40 to-background"
        />
        <ChristmasStrip className="px-0 pb-4 pt-0" />
        <ProductListing
          scope={{ mode: "new", cacheKey: "new" }}
          search={search}
          onChange={(next) => navigate({ search: (prev: typeof search) => ({ ...prev, ...next }), replace: true })}
        />
      </div>
    </StorefrontLayout>
  );
}
